
import { useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchShoppingItems } from "@/services/shopping/items/index";
import { ShoppingItem, ShoppingItemWithProduct } from "@/types";
import { getVisibleUserCategories } from "@/services/categories.service";
import { DropResult } from "@hello-pangea/dnd";
import { networkService } from '@/services/network.service';

export function useShoppingItemsAndCategories(listId: string) {
  // Fetch items for the shopping list
  const {
    data: items,
    isLoading: itemsLoading,
    error: itemsError
  } = useQuery<ShoppingItemWithProduct[]>({
    queryKey: ["shopping-items", listId],
    queryFn: () => fetchShoppingItems(listId),
    enabled: !!listId,
    // Manteniamo i dati per 5 minuti per ridurre le chiamate ripetitive
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Fetch visible categories 
  const {
    data: userCategories,
    isLoading: categoriesLoading,
    error: categoriesError
  } = useQuery({
    queryKey: ["visible-user-categories"],
    queryFn: getVisibleUserCategories,
    // Manteniamo i dati in cache per 5 minuti
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Optimistic state - only for offline mode
  const [optimisticItems, setOptimisticItems] = useState<ShoppingItemWithProduct[]>([]);
  const [isManualSorting, setIsManualSorting] = useState(false);

  const setOptimisticItemsWithTracking = useCallback((updater: React.SetStateAction<ShoppingItemWithProduct[]>) => {
    // Always update optimistic items so the latest state is preserved for offline use
    setOptimisticItems(updater);
  }, []);

  // Handle drag and drop to reorder items
  const handleDragEnd = useCallback((result: DropResult) => {
    if (!result.destination) return;

    setIsManualSorting(true);

    // Get the current items (server + optimistic)
    const serverItems = items || [];
    const allItems = [
      ...serverItems,
      ...optimisticItems.filter(item => !serverItems.some(s => s.id === item.id))
    ];

    const reorderItems = Array.from(allItems);
    const [movedItem] = reorderItems.splice(result.source.index, 1);
    reorderItems.splice(result.destination.index, 0, movedItem);

    // Update positions for all items
    const updatedItems = reorderItems.map((item, index) => ({
      ...item,
      position: index
    }));

    // Update optimistic items with new positions only if offline
    if (!networkService.isOnline()) {
      const updatedOptimisticItems = updatedItems.filter(item => 
        optimisticItems.some(opt => opt.id === item.id)
      );
      setOptimisticItemsWithTracking(updatedOptimisticItems);
    }
  }, [items, optimisticItems, setOptimisticItemsWithTracking]);

  const handleDragEndWrapper = useCallback((result: DropResult) => {
    handleDragEnd(result);
  }, [handleDragEnd]);

  // Determine loading and error states
  const isLoading = itemsLoading || categoriesLoading;
  const error = itemsError || categoriesError;

  return {
    userCategories: userCategories || [],
    optimisticItems,
    setOptimisticItems: setOptimisticItemsWithTracking,
    isManualSorting,
    setIsManualSorting,
    handleDragEndWrapper,
    isLoading,
    error
  };
}
